import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useProjects } from '../hooks/useProjects';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/apiClient';
import { ClipboardIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CodeEditor from '../components/CodeEditor';
import { OPENAI_CHAT_MODEL_OPTIONS } from '../constants/openaiModels';
import { CLAUDE_MODEL_OPTIONS, GEMINI_MODEL_OPTIONS } from '../constants/providerModels';

// DiffView component for displaying prompt modifications
const DiffView = ({ diffData, originalPrompt, onChange }) => {
  const [message, setMessage] = useState(null);
  const [acceptedChanges, setAcceptedChanges] = useState({
    additions: {},
    deletions: {},
    replacements: {}
  });
  
  useEffect(() => {
    // When diffData changes, initialize all changes as accepted by default
    if (diffData) {
      const newAcceptedChanges = {
        additions: {},
        deletions: {},
        replacements: {}
      };
      
      // Set all additions as accepted by default
      if (Array.isArray(diffData.additions)) {
        diffData.additions.forEach((addition, index) => {
          newAcceptedChanges.additions[index] = true;
        });
      }
      
      // Set all deletions as accepted by default
      if (Array.isArray(diffData.deletions)) {
        diffData.deletions.forEach((deletion, index) => {
          newAcceptedChanges.deletions[index] = true;
        });
      }
      
      // Set all replacements as accepted by default
      if (Array.isArray(diffData.replacements)) {
        diffData.replacements.forEach((replacement, index) => {
          newAcceptedChanges.replacements[index] = true;
        });
      }
      
      setAcceptedChanges(newAcceptedChanges);
    }
  }, [diffData]);
  
  // Generate updated prompt based on accepted changes only
  useEffect(() => {
    if (diffData && originalPrompt && onChange) {
      let updatedPrompt = originalPrompt;
      
      // Process only the accepted changes
      
      // Apply accepted replacements first (to avoid position shifting)
      if (Array.isArray(diffData.replacements)) {
        diffData.replacements.forEach((replacement, index) => {
          if (acceptedChanges.replacements[index]) {
            updatedPrompt = replaceTextAt(
              updatedPrompt, 
              replacement.position, 
              replacement.oldText, 
              replacement.newText
            );
          }
        });
      }
      
      // Then apply accepted deletions
      if (Array.isArray(diffData.deletions)) {
        diffData.deletions.forEach((deletion, index) => {
          if (acceptedChanges.deletions[index]) {
            updatedPrompt = removeTextAt(
              updatedPrompt, 
              deletion.position, 
              deletion.text
            );
          }
        });
      }
      
      // Finally apply accepted additions
      if (Array.isArray(diffData.additions)) {
        diffData.additions.forEach((addition, index) => {
          if (acceptedChanges.additions[index]) {
            updatedPrompt = insertTextAt(
              updatedPrompt, 
              addition.position, 
              addition.text
            );
          }
        });
      }
      
      // Notify parent component of the updated prompt
      onChange(updatedPrompt);
    }
  }, [diffData, originalPrompt, acceptedChanges, onChange]);
  
  // Helper function to insert text at a specific position
  const insertTextAt = (text, position, insertedText) => {
    // Check if we need to add a newline before the inserted text
    const needsLeadingNewline = position > 0 && text.charAt(position - 1) !== '\n' && !insertedText.startsWith('\n');
    
    // Check if we need to add a newline after the inserted text
    const needsTrailingNewline = position < text.length && text.charAt(position) !== '\n' && !insertedText.endsWith('\n');
    
    // Add necessary newlines
    const formattedText = 
      (needsLeadingNewline ? '\n' : '') + 
      insertedText + 
      (needsTrailingNewline ? '\n' : '');
    
    return text.substring(0, position) + formattedText + text.substring(position);
  };
  
  // Helper function to remove text at a specific position
  const removeTextAt = (text, position, textToRemove) => {
    return text.substring(0, position) + text.substring(position + textToRemove.length);
  };
  
  // Helper function to replace text at a specific position
  const replaceTextAt = (text, position, oldText, newText) => {
    return text.substring(0, position) + newText + text.substring(position + oldText.length);
  };
  
  // Toggle acceptance status of a change
  const toggleChangeAcceptance = (type, index) => {
    setAcceptedChanges(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [index]: !prev[type][index]
      }
    }));
  };
  
  useEffect(() => {
    // Check if diffData exists but has no actual modifications
    if (diffData) {
      const additionsEmpty = !Array.isArray(diffData.additions) || diffData.additions.length === 0;
      const deletionsEmpty = !Array.isArray(diffData.deletions) || diffData.deletions.length === 0;
      const replacementsEmpty = !Array.isArray(diffData.replacements) || diffData.replacements.length === 0;
      
      console.log('DiffView checking for empty diffData:', {
        additionsEmpty,
        deletionsEmpty,
        replacementsEmpty,
        additionsLength: Array.isArray(diffData.additions) ? diffData.additions.length : 0,
        deletionsLength: Array.isArray(diffData.deletions) ? diffData.deletions.length : 0,
        replacementsLength: Array.isArray(diffData.replacements) ? diffData.replacements.length : 0
      });
      
      if (additionsEmpty && deletionsEmpty && replacementsEmpty) {
        setMessage("No differences detected between the original and generated prompt.");
      } else {
        setMessage(null);
      }
    } else if (!diffData || !originalPrompt) {
      setMessage("No diff data available.");
    } else {
      setMessage(null);
    }
  }, [diffData, originalPrompt]);
  
  if (message) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-yellow-800">{message}</p>
        </div>
        <p className="mt-2 text-sm text-yellow-600 pl-8">
          This could mean that no changes were needed, or that the AI assistant didn't properly mark its modifications. 
          Try reviewing the final prompt manually to see if any changes were made.
        </p>
      </div>
    );
  }
  
  if (!diffData || !originalPrompt) return null;
  
  // Create a visual representation of the diff
  const { additions = [], deletions = [], replacements = [] } = diffData;
  
  // Log the diffData for debugging
  console.log('DiffView rendering with data:', {
    additions: additions.length,
    deletions: deletions.length,
    replacements: replacements.length,
    firstAddition: additions[0],
    firstReplacement: replacements[0]
  });
  
  // Create an array of line objects for rendering
  const lines = originalPrompt.split('\n').map((line, index) => ({
    content: line,
    lineNumber: index + 1,
    type: 'unchanged'
  }));
  
  // Helper function to find the line number for a position in the text
  const findLineForPosition = (position) => {
    const textUpToPosition = originalPrompt.substring(0, position);
    return (textUpToPosition.match(/\n/g) || []).length + 1;
  };
  
  // Mark deletions
  if (Array.isArray(deletions)) {
    deletions.forEach((deletion, idx) => {
      const lineNumber = findLineForPosition(deletion.position);
      const lineIndex = lineNumber - 1;
      
      if (lineIndex >= 0 && lineIndex < lines.length) {
        if (lines[lineIndex].content.includes(deletion.text)) {
          lines[lineIndex].type = 'deletion';
          lines[lineIndex].highlight = deletion.text;
          lines[lineIndex].changeIndex = idx;
          lines[lineIndex].accepted = acceptedChanges.deletions[idx];
          // Add section identifier for grouping
          const sectionMatch = lines[lineIndex].content.match(/^([A-Z][A-Z\s_]+)(?:\s*:|$)/);
          if (sectionMatch) {
            lines[lineIndex].sectionName = sectionMatch[1].trim();
          }
        }
      }
    });
  }
  
  // Mark replacements
  if (Array.isArray(replacements)) {
    replacements.forEach((replacement, idx) => {
      const lineNumber = findLineForPosition(replacement.position);
      const lineIndex = lineNumber - 1;
      
      if (lineIndex >= 0 && lineIndex < lines.length) {
        if (lines[lineIndex].content.includes(replacement.oldText)) {
          lines[lineIndex].type = 'replacement';
          lines[lineIndex].highlight = replacement.oldText;
          lines[lineIndex].replacement = replacement.newText;
          lines[lineIndex].changeIndex = idx;
          lines[lineIndex].accepted = acceptedChanges.replacements[idx];
          // Add section identifier for grouping
          const sectionMatch = lines[lineIndex].content.match(/^([A-Z][A-Z\s_]+)(?:\s*:|$)/);
          if (sectionMatch) {
            lines[lineIndex].sectionName = sectionMatch[1].trim();
          }
        }
      }
    });
  }
  
  // Add insertions (this is more complex as they might be between lines)
  if (Array.isArray(additions)) {
    // Pre-process additions to handle special ordering cases
    const processedAdditions = [...additions].map(addition => {
      // Clone the addition object to avoid modifying the original
      const processedAddition = {...addition};
      
      // Check if this addition is an example response section (using generic matching)
      // Look for patterns like "EXAMPLE X RESPONSE" that should appear at the end
      if (typeof addition.text === 'string' && 
          addition.text.match(/EXAMPLE\s+[A-Z\s_]+RESPONSE/i) &&
          // Also check for presence of JSON-like content with { }
          addition.text.includes('{') && addition.text.includes('}')) {
        // Force this type of section to appear at the end of the document
        processedAddition.position = Number.MAX_SAFE_INTEGER;
      }
      
      return processedAddition;
    });
    
    // Sort additions by position to ensure correct order
    processedAdditions.sort((a, b) => a.position - b.position);
    
    // Now process each addition
    processedAdditions.forEach((addition, idx) => {
      const lineNumber = findLineForPosition(addition.position);
      const lineIndex = lineNumber - 1;
      
      if (lineIndex >= 0 && lineIndex <= lines.length) {
        // Split the addition text by newlines to handle multi-line additions properly
        const addedLines = addition.text.split('\n');
        
        // Try to identify the section name from the addition text
        let sectionName = null;
        const sectionMatch = addition.text.match(/([A-Z][A-Z\s_]+)(?:\s*:|$)/m);
        if (sectionMatch) {
          sectionName = sectionMatch[1].trim();
        }
        
        // Insert each line of the addition as a separate line object
        addedLines.forEach((lineText, addedLineIndex) => {
          // Find the original index to ensure the accepted state is tracked correctly
          const originalIndex = additions.findIndex(orig => orig.text === addition.text);
          const additionIdx = originalIndex >= 0 ? originalIndex : idx;
          
          lines.splice(lineIndex + addedLineIndex + 1, 0, {
            content: lineText,
            lineNumber: lineNumber + (addedLineIndex / (addedLines.length + 1)), // Fractional line numbers for insertion order
            type: 'addition',
            changeIndex: additionIdx,
            accepted: acceptedChanges.additions[additionIdx],
            // If first line of a section or contains section header, add the section name
            sectionName: sectionName
          });
        });
      }
    });
  }
  
  // Sort lines by line number
  lines.sort((a, b) => a.lineNumber - b.lineNumber);
  
  // Group lines by section
  const sections = {};
  let currentSection = null;
  
  lines.forEach((line, index) => {
    // Check if this line starts a new section
    const sectionHeaderMatch = line.content.match(/^([A-Z][A-Z\s_]+)(?:\s*:|$)/);
    if (sectionHeaderMatch) {
      currentSection = sectionHeaderMatch[1].trim();
      if (!sections[currentSection]) {
        sections[currentSection] = {
          name: currentSection,
          lines: [],
          types: new Set(),
          changeIndices: {}
        };
      }
    }
    
    // If we have an identified section, add the line to it
    if (currentSection) {
      if (!sections[currentSection]) {
        sections[currentSection] = {
          name: currentSection,
          lines: [],
          types: new Set(),
          changeIndices: {}
        };
      }
      
      // Add line to the section
      sections[currentSection].lines.push(index);
      
      // Track change types and indices for this section
      if (line.type !== 'unchanged') {
        sections[currentSection].types.add(line.type);
        
        // Track change indices by type
        if (line.changeIndex !== undefined) {
          if (!sections[currentSection].changeIndices[line.type]) {
            sections[currentSection].changeIndices[line.type] = new Set();
          }
          sections[currentSection].changeIndices[line.type].add(line.changeIndex);
        }
      }
    }
    
    // Assign line's section
    line.section = currentSection;
    
    // Assign proper line numbers
    line.displayNumber = index + 1;
  });
  
  // Convert sections to array
  const sectionsArray = Object.values(sections);
  
  // Function to toggle all changes in a section
  const toggleSectionChanges = (sectionName, accept) => {
    const section = sections[sectionName];
    if (!section) return;
    
    const newAcceptedChanges = {...acceptedChanges};
    
    // Update all change types in this section
    for (const type of section.types) {
      const typeKey = type === 'addition' ? 'additions' : 
                    type === 'deletion' ? 'deletions' : 'replacements';
                    
      // Update all change indices for this type
      if (section.changeIndices[type]) {
        for (const changeIndex of section.changeIndices[type]) {
          newAcceptedChanges[typeKey][changeIndex] = accept;
        }
      }
    }
    
    setAcceptedChanges(newAcceptedChanges);
  };
  
  // Check if we have any actual changes to show
  const hasChanges = lines.some(line => line.type !== 'unchanged');
  
  if (!hasChanges) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-yellow-800">No visible changes detected in the prompt.</p>
        </div>
        <p className="mt-2 text-sm text-yellow-600 pl-8">
          Try reviewing the final prompt manually to see if any changes were made that weren't properly marked with tags.
        </p>
      </div>
    );
  }
  
  // Controls to accept/reject all changes at once
  const acceptAllChanges = () => {
    const newAcceptedChanges = {
      additions: {},
      deletions: {},
      replacements: {}
    };
    
    if (Array.isArray(diffData.additions)) {
      diffData.additions.forEach((_, index) => {
        newAcceptedChanges.additions[index] = true;
      });
    }
    
    if (Array.isArray(diffData.deletions)) {
      diffData.deletions.forEach((_, index) => {
        newAcceptedChanges.deletions[index] = true;
      });
    }
    
    if (Array.isArray(diffData.replacements)) {
      diffData.replacements.forEach((_, index) => {
        newAcceptedChanges.replacements[index] = true;
      });
    }
    
    setAcceptedChanges(newAcceptedChanges);
  };
  
  const rejectAllChanges = () => {
    const newAcceptedChanges = {
      additions: {},
      deletions: {},
      replacements: {}
    };
    
    if (Array.isArray(diffData.additions)) {
      diffData.additions.forEach((_, index) => {
        newAcceptedChanges.additions[index] = false;
      });
    }
    
    if (Array.isArray(diffData.deletions)) {
      diffData.deletions.forEach((_, index) => {
        newAcceptedChanges.deletions[index] = false;
      });
    }
    
    if (Array.isArray(diffData.replacements)) {
      diffData.replacements.forEach((_, index) => {
        newAcceptedChanges.replacements[index] = false;
      });
    }
    
    setAcceptedChanges(newAcceptedChanges);
  };
  
  return (
    <div className="font-mono text-sm bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
      <div className="p-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center">
        <div className="flex space-x-6 text-xs text-gray-500">
          <div className="flex items-center">
            <span className="h-3 w-3 bg-green-200 border border-green-400 rounded-sm mr-1"></span>
            <span>Additions</span>
          </div>
          <div className="flex items-center">
            <span className="h-3 w-3 bg-red-200 border border-red-400 rounded-sm mr-1"></span>
            <span>Deletions</span>
          </div>
          <div className="flex items-center">
            <span className="h-3 w-3 bg-yellow-200 border border-yellow-400 rounded-sm mr-1"></span>
            <span>Replacements</span>
          </div>
        </div>
        <div className="flex space-x-2">
          <button 
            onClick={acceptAllChanges}
            className="btn btn-primary px-2 py-1 bg-green-500 text-white text-smd rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 mr-2"
          >
            Accept All
          </button>
          <button 
            onClick={rejectAllChanges}
            className="btn btn-secondary px-2 py-1 bg-red-500 text-white text-smd rounded hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Reject All
          </button>
        </div>
      </div>
      
      
      
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <tbody>
            {/* Track sections we've already displayed headers for */}
            {(() => {
              const displayedSectionHeaders = new Set();
              
              // We'll map over the lines and insert section headers where needed
              return lines.map((line, index) => {
                // Variables to track if we need to show a section header
                let showSectionHeader = false;
                let currentSection = null;
                
                // Check if this is the first line of a section with changes
                if (line.section && !displayedSectionHeaders.has(line.section)) {
                  const section = sections[line.section];
                  
                  // Only show headers for sections with changes
                  if (section && section.types.size > 0) {
                    showSectionHeader = true;
                    currentSection = section;
                    displayedSectionHeaders.add(line.section);
                    
                    // Calculate if all changes in this section are accepted or rejected
                    let allAccepted = true;
                    let allRejected = true;
                    
                    // Check addition acceptance
                    if (section.changeIndices.addition) {
                      for (const changeIdx of section.changeIndices.addition) {
                        if (!acceptedChanges.additions[changeIdx]) allAccepted = false;
                        if (acceptedChanges.additions[changeIdx]) allRejected = false;
                      }
                    }
                    
                    // Check deletion acceptance
                    if (section.changeIndices.deletion) {
                      for (const changeIdx of section.changeIndices.deletion) {
                        if (!acceptedChanges.deletions[changeIdx]) allAccepted = false;
                        if (acceptedChanges.deletions[changeIdx]) allRejected = false;
                      }
                    }
                    
                    // Check replacement acceptance
                    if (section.changeIndices.replacement) {
                      for (const changeIdx of section.changeIndices.replacement) {
                        if (!acceptedChanges.replacements[changeIdx]) allAccepted = false;
                        if (acceptedChanges.replacements[changeIdx]) allRejected = false;
                      }
                    }
                    
                    // Return a section header row followed by the current line
                    return (
                      <React.Fragment key={`section-${line.section}-${index}`}>
                        <tr className="bg-blue-50 border-t border-b border-blue-200">
                          <td colSpan="2" className="py-2 px-4">
                            <div className="flex justify-between items-center">
                              <h3 className="font-medium text-blue-800">{line.section}</h3>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => toggleSectionChanges(line.section, true)}
                                  className={`px-2 py-1 text-smd rounded btn btn-primary mr-2 ${
                                    allAccepted ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-500 hover:text-white'
                                  }`}
                                >
                                  Accept Section
                                </button>
                                <button
                                  onClick={() => toggleSectionChanges(line.section, false)}
                                  className={`px-2 py-1 text-smd rounded btn btn-secondary ${
                                    allRejected ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800 hover:bg-red-500 hover:text-white'
                                  }`}
                                >
                                  Reject Section
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                        <tr 
                          className={`
                            ${line.type === 'unchanged' ? 'bg-white-custom' : 
                              line.type === 'deletion' ? 'bg-red-custom' : 
                              line.type === 'addition' ? 'bg-green-custom' : 
                              line.type === 'replacement' ? 'bg-yellow-custom' : ''}
                            ${!line.accepted && line.type !== 'unchanged' ? 'opacity-50' : ''}
                            ${line.section ? `section-${line.section.replace(/\s+/g, '-')}` : ''}
                            hover:bg-gray-custom
                          `}
                        >
                          <td className="py-1 px-2 text-gray-500 select-none text-right border-r w-10">
                            {Math.floor(line.displayNumber)}
                          </td>
                          <td className="py-1 px-4 whitespace-pre-wrap">
                            {line.type === 'unchanged' && (
                              <span>{line.content}</span>
                            )}
                            
                            {line.type === 'deletion' && (
                              <div className="flex items-center">
                                <span className={`line-through ${line.accepted ? 'text-red-800' : 'text-gray-500'} font-bold`}>
                                  {line.content}
                                </span>
                              </div>
                            )}
                            
                            {line.type === 'addition' && (
                              <div className="flex items-center">
                                <span className={`${line.accepted ? 'text-green-800' : 'text-gray-500'} font-bold`}>
                                  {line.content}
                                </span>
                              </div>
                            )}
                            
                            {line.type === 'replacement' && (
                              <div className="flex items-center">
                                <div>
                                  <span className={`line-through ${line.accepted ? 'text-red-800' : 'text-gray-500'} font-bold`}>
                                    {line.highlight}
                                  </span>
                                  <span className="mx-2 text-gray-500">→</span>
                                  <span className={`${line.accepted ? 'text-green-800' : 'text-gray-500'} font-bold`}>
                                    {line.replacement}
                                  </span>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  }
                }
                
                // Regular line (not a section header)
                return (
                  <tr 
                    key={index}
                    className={`
                      ${line.type === 'unchanged' ? 'bg-white-custom' : 
                        line.type === 'deletion' ? 'bg-red-custom' : 
                        line.type === 'addition' ? 'bg-green-custom' : 
                        line.type === 'replacement' ? 'bg-yellow-custom' : ''}
                      ${!line.accepted && line.type !== 'unchanged' ? 'opacity-50' : ''}
                      ${line.section ? `section-${line.section.replace(/\s+/g, '-')}` : ''}
                      hover:bg-gray-custom
                    `}
                  >
                    <td className="py-1 px-2 text-gray-500 select-none text-right border-r w-10">
                      {Math.floor(line.displayNumber)}
                    </td>
                    <td className="py-1 px-4 whitespace-pre-wrap">
                      {line.type === 'unchanged' && (
                        <span>{line.content}</span>
                      )}
                      
                      {line.type === 'deletion' && (
                        <div className="flex items-center">
                          <span className={`line-through ${line.accepted ? 'text-red-800' : 'text-gray-500'} font-bold`}>
                            {line.content}
                          </span>
                        </div>
                      )}
                      
                      {line.type === 'addition' && (
                        <div className="flex items-center">
                          <span className={`${line.accepted ? 'text-green-800' : 'text-gray-500'} font-bold`}>
                            {line.content}
                          </span>
                        </div>
                      )}
                      
                      {line.type === 'replacement' && (
                        <div className="flex items-center">
                          <div>
                            <span className={`line-through ${line.accepted ? 'text-red-800' : 'text-gray-500'} font-bold`}>
                              {line.highlight}
                            </span>
                            <span className="mx-2 text-gray-500">→</span>
                            <span className={`${line.accepted ? 'text-green-800' : 'text-gray-500'} font-bold`}>
                              {line.replacement}
                            </span>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PromptBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Add model selection state
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  
  // Add CSS classes for diff colors
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .bg-red-custom {
        background-color: rgba(254, 202, 202, 0.5);
      }
      .bg-green-custom {
        background-color: rgba(187, 247, 208, 0.5);
      }
      .bg-yellow-custom {
        background-color: rgba(254, 240, 138, 0.5);
      }
      .bg-gray-custom {
        background-color: rgba(243, 244, 246, 0.5);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Fetch project data
  const { data: projectData, isLoading, error, refetch } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await apiClient.get(`/projects/${projectId}`);
      console.log("Raw API Response:", response);
      return response;
    }
  });

  // Handle project data changes
  useEffect(() => {
    if (projectData?.data?.project) {
      console.log("Project data updated:", projectData);
      const project = projectData.data.project;
      
      // Set available models based on API key availability
      const models = [];
      
      // OpenAI models (available with OpenAI key or AI/ML API key)
      if (project.openAIConfig?.apiKey || project.aimlapiConfig?.apiKey) {
        models.push(
          ...OPENAI_CHAT_MODEL_OPTIONS.map((model) => ({
            id: model.value,
            name: model.label
          }))
        );
      }
      
      // DeepSeek models (available with DeepSeek key or AI/ML API key)
      if (project.deepseekConfig?.apiKey || project.aimlapiConfig?.apiKey) {
        models.push({ id: 'deepseek-chat', name: 'DeepSeek V3' });
      }
      
      // AI/ML API exclusive models (only available with AI/ML API key)
      if (project.aimlapiConfig?.apiKey) {
        models.push(
          // Qwen models (simple names)
          { id: 'qwen-max', name: 'Qwen Max' },
          { id: 'qwen-plus', name: 'Qwen Plus' },
          { id: 'qwen-turbo', name: 'Qwen Turbo' },
          // Claude models (simple names)
          ...CLAUDE_MODEL_OPTIONS.map((model) => ({ id: model.value, name: model.label })),
          ...GEMINI_MODEL_OPTIONS.map((model) => ({ id: model.value, name: model.label })),
          // Gemma models
          { id: 'gemma-2', name: 'Gemma 2' },
          { id: 'gemma-3', name: 'Gemma 3' },
          // Meta Llama models
          { id: 'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo', name: 'Llama 3.1 8B Instruct Turbo' },
          { id: 'meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo', name: 'Llama 3.1 70B Instruct Turbo' },
          { id: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo', name: 'Llama 3.1 405B Instruct Turbo' },
          { id: 'meta-llama/Llama-3.3-70B-Instruct-Turbo', name: 'Llama 3.3 70B Instruct Turbo' },
          { id: 'meta-llama/llama-4-scout', name: 'Llama 4 Scout' },
          { id: 'meta-llama/llama-4-maverick', name: 'Llama 4 Maverick' },
          // NVIDIA models
          { id: 'nvidia/Llama-3.1-Nemotron-70B-Instruct-HF', name: 'Llama 3.1 Nemotron 70B Instruct' }
        );
      }
      
      setAvailableModels(models);
      if (models.length > 0) {
        setSelectedModel(models[0].id);
      }
    }
  }, [projectData]);
  
  // Define mutation for updating projects
  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return await apiClient.patch(`/projects/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project', projectId]);
      setSaveSuccess(true);
    }
  });
  
  // Basic state management
  const [activeTab, setActiveTab] = useState('primary');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [apiAnalysis, setApiAnalysis] = useState(null);
  const [promptDescription, setPromptDescription] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [promptGenerationMode, setPromptGenerationMode] = useState('new'); // 'new' or 'modify'
  const [activePromptType, setActivePromptType] = useState('primary');
  const [hasGenerationError, setHasGenerationError] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [showDiffView, setShowDiffView] = useState(false);
  
  // Add new state for raw response view
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [rawResponse, setRawResponse] = useState('');
  
  // Function to toggle between diff view and raw response view
  const toggleRawResponseView = () => {
    setShowRawResponse(!showRawResponse);
  };
  
  // Define mutation for generating prompts
  const generatePromptMutation = useMutation({
    mutationFn: async ({ projectId, promptData }) => {
      return await apiClient.post(`/projects/${projectId}/prompts/generate`, {
        ...promptData,
        model: selectedModel
      });
    },
    onError: (error) => {
      console.error('Error generating prompt:', error);
      toast.error(`Failed to generate prompt: ${error.message || 'Unknown error'}`);
      setHasGenerationError(true);
    }
  });
  
  const defaultDecisionMakerPrompt = `You are {{CHATBOT_NAME}}, an AI chatbot designed to assist users with their inquiries. Your goal is to accurately determine user intent, select an appropriate action, and handle the conversation effectively. When deciding on an action, follow these guidelines:

IDENTITY:
- Your name is {{CHATBOT_NAME}}
- When users ask who you are, respond with your name and purpose
- Always maintain a consistent identity

RESPONSE TYPES:

1. DIRECT RESPONSE: Use when you can directly answer the user's question without needing to call an API or search for additional information.
Example structure:
{
  "intent": "greeting",
  "action": "direct_response",
  "thought": "User sent a greeting, I'll respond directly.",
  "input_language": "en",
  "message": "Hello! How can I help you today?"
}

2. API CALL: Use when you need to retrieve specific information or perform actions through an API.
Example structure:
{
  "intent": "check_status",
  "action": "api_call",
  "thought": "User wants to check their order status. I should call the appropriate API.",
  "input_language": "en",
  "api_endpoint": "http://domainname.com/api/orders/status",
  "api_name" : "Get Order Status",
  "parameters": {
    "orderId": "{{order_id}}"
  },
  "requires_followup": true
}

3. KNOWLEDGE SEARCH: Use when you need to search for information in the project's knowledge base.
Example structure:
{
  "intent": "product_information",
  "action": "knowledge_search",
  "thought": "User is asking about product specifications, I should search the knowledge base.",
  "input_language": "en",
  "search_query": "detailed specifications of ModelX product",
  "requires_followup": true
}

4. MULTI-STEP REASONING: Use when you need to break down a complex problem into steps.
Example structure:
{
  "intent": "complex_calculation",
  "action": "multi_step_reasoning",
  "thought": "This requires multiple calculation steps. I'll break it down.",
  "input_language": "en",
  "reasoning_steps": [
    "First, I need to calculate the base price.",
    "Then, I need to apply any applicable discounts.",
    "Finally, I'll add tax to get the final price."
  ],
  "requires_followup": true
}

5. LOCATION FINDER: Use when the user is asking about finding nearby locations or services.
Example structure:
{
  "intent": "find_nearest_location",
  "action": "api_call",
  "thought": "User wants to find the nearest location. I need their coordinates to help them.",
  "api_endpoint": "geocode",
  "parameters": {
    "projectId": "{{projectId}}",
    "query": "{{user_location_text}}"
  },
  "requires_followup": true
}

LOCATION-BASED QUERIES:
When handling requests about finding nearby locations (particularly hospitals like "Mitra Keluarga"), follow this process:

1. FIRST STEP: Check if location data exists in the chatSession's userData.metadata:
   - If latitude and longitude are present, use them directly
   - If only an address is present, use the geocode API to convert it to coordinates
   - If no location data is present and user mentions a location in text form, use the geocode API

2. SECOND STEP: After obtaining coordinates (either from metadata or geocoding), use them to find the nearest location.

Example 1 - When coordinates exist in metadata and user asks about their current location:
User: "Where is the nearest Mitra Keluarga hospital?"
Your response should be:
{
  "intent": "find_nearest_location",
  "action": "api_call",
  "thought": "User has coordinates in metadata, so I can directly find the nearest hospital.",
  "input_language": "en",
  "api_endpoint": "nearest",
  "parameters": {
    "latitude": "{{userData.metadata.latitude}}",
    "longitude": "{{userData.metadata.longitude}}",
    "keyword": "Mitra Keluarga",
    "limit": 3
  },
  "requires_followup": true
}

Example 1a - When presenting location results with Google Maps links:
When responding to the user with location information, if a location has a Google Maps link, always include it in the response. For example:

"The nearest Mitra Keluarga hospital to your location is Mitra Keluarga Kemang, which is 2.3 km away. It's located at Jl. Kemang Raya No. 35, South Jakarta. You can call them at (021) 7194-9630. 

For directions, you can use this Google Maps link: [Google Maps Link]"

Example 2 - When only address exists in metadata:
User: "Where is the nearest Mitra Keluarga hospital?"
Your response should be:
{
  "intent": "geocode_location",
  "action": "api_call",
  "thought": "User has an address in metadata but no coordinates. I need to geocode the address first.",
  "input_language": "en",
  "api_endpoint": "geocode",
  "parameters": {
    "projectId": "{{projectId}}",
    "query": "{{userData.metadata.address}}"
  },
  "requires_followup": true
}

Example 3 - When user mentions a specific location in their message:
User: "Where is the closest Mitra Keluarga hospital to Bekasi?"
Your response should be:
{
  "intent": "geocode_location",
  "action": "api_call",
  "thought": "User specified 'Bekasi' in their message. I should use this location instead of metadata coordinates.",
  "input_language": "en",
  "api_endpoint": "geocode",
  "parameters": {
    "projectId": "{{projectId}}",
    "query": "Bekasi, Jakarta"
  },
  "requires_followup": true
}

Example 4 - After geocoding a location mentioned in the message:
Your response should be:
{
  "intent": "find_nearest_location",
  "action": "api_call",
  "thought": "Now I have the coordinates, I can find the nearest Mitra Keluarga hospital.",
  "input_language": "en",
  "api_endpoint": "nearest",
  "parameters": {
    "latitude": "{{coordinates.latitude}}",
    "longitude": "{{coordinates.longitude}}",
    "keyword": "Mitra Keluarga",
    "limit": 3
  },
  "requires_followup": true
}

Example 5 - When no location data is available:
User: "Where is the nearest Mitra Keluarga hospital?"
Your response should be:
{
  "intent": "request_location",
  "action": "direct_response",
  "thought": "User wants to find the nearest Mitra Keluarga hospital but no location data is available.",
  "input_language": "en",
  "message": "To help you find the nearest Mitra Keluarga hospital, I need to know your location. Could you please tell me which area you're in or interested in?"
}

IMPORTANT: NEVER invent or make up location data. Only use data returned from API calls or provided in the metadata. If the API doesn't return hospital information, acknowledge this and ask for clarification rather than making up fictional hospital data.

DECISION PROCESS:
1. Analyze the user's message to identify the main intent
2. Determine if you have all necessary information to respond
3. Choose the most appropriate action based on the intent
4. Include any required parameters for the action
5. Indicate if follow-up is required

EXAMPLES:

Example 1 - When user asks a general question:
User: "What are your business hours?"
Your response should be:
{
  "intent": "business_hours",
  "action": "direct_response",
  "thought": "User wants to know business hours. I can answer this directly from my knowledge.",
  "input_language": "en",
  "message": "Our business hours are Monday to Friday from 9am to 5pm, and Saturday from 10am to 2pm. We are closed on Sundays."
}

Example 2 - When user needs specific data:
User: "Can you check if my order #12345 has shipped?"
Your response should be:
{
  "intent": "order_status",
  "action": "api_call",
  "thought": "User wants to check order status. I need to call the order status API.",
  "input_language": "en",
  "api_endpoint": "/api/orders/status",
  "parameters": {
    "orderId": "12345"
  },
  "requires_followup": true
}

Example 3 - When user asks about product information:
User: "Tell me about your premium subscription plan"
Your response should be:
{
  "intent": "product_information",
  "action": "knowledge_search",
  "thought": "User wants information about the premium subscription plan. I should search our knowledge base.",
  "input_language": "en",
  "search_query": "premium subscription plan features pricing",
  "requires_followup": true
}

Example 4 - When user asks about nearest hospital with location text:
User: "Where is the closest Mitra Keluarga hospital to Kemang area?"
Your response should be:
{
  "intent": "geocode_location",
  "action": "api_call",
  "thought": "User wants to find the nearest Mitra Keluarga hospital to Kemang. First, I need to geocode 'Kemang' to get coordinates.",
  "input_language": "en",
  "api_endpoint": "geocode",
  "parameters": {
    "projectId": "67c975e0332631a14b0d922c",
    "query": "Kemang, Jakarta"
  },
  "requires_followup": true
}

Example 5 - After geocoding is complete:
Your response should be:
{
  "intent": "find_nearest_location",
  "action": "api_call",
  "thought": "Now I have the coordinates for Kemang, I can find the nearest Mitra Keluarga hospital.",
  "input_language": "en",
  "api_endpoint": "nearest",
  "parameters": {
    "latitude": "-6.260697",
    "longitude": "106.813934",
    "keyword": "Mitra Keluarga",
    "limit": 3
  },
  "requires_followup": true
}

Example 6 - When user doesn't provide location:
User: "Where is the nearest Mitra Keluarga hospital?"
Your response should be:
{
  "intent": "request_location",
  "action": "direct_response",
  "thought": "User wants to find the nearest Mitra Keluarga hospital but hasn't provided location info.",
  "input_language": "en",
  "message": "To help you find the nearest Mitra Keluarga hospital, I need to know your location. Could you please tell me which area you're in or interested in?"
}

IMPORTANT GUIDELINES:
- Always accurately identify user intent before deciding on an action
- Only include parameters that are absolutely necessary
- If you're missing crucial information, use a direct response to ask for it
- Always include clear reasoning in the "thought" field
- For complex tasks, consider using multi-step reasoning
- When users ask about nearby locations, follow the two-step process: geocode first (if needed), then find nearest location

Remember, your primary goal is to assist the user effectively by choosing the most appropriate action based on their intent.`;

  const defaultResponseGeneratorPrompt = `You are {{CHATBOT_NAME}}, an AI chatbot focused on generating helpful, natural responses based on the information provided. Your task is to craft conversational and informative replies using the context given to you.

IDENTITY:
- Your name is {{CHATBOT_NAME}}
- When users ask who you are, respond with your name and purpose
- Always maintain a consistent identity

RESPONSE GUIDELINES:
- Respond in a natural, conversational tone
- Be concise but thorough - avoid unnecessary verbosity
- Maintain a friendly, professional demeanor
- Format information clearly using paragraphs and bullet points when appropriate
- If information is limited or uncertain, acknowledge this
- Always tailor your response to the user's original question

For API responses:
- Extract the relevant information from the API response
- Format it in a user-friendly way
- Provide context and explanation where needed
- If the API returned an error, explain the issue clearly
- For empty results, provide helpful alternatives

For Knowledge Base results:
- Synthesize information from multiple knowledge sources when available
- Cite sources if appropriate
- Present information logically
- Highlight the most relevant details first

For Location search results:
- Clearly present the nearest location first
- Include distance and travel time information
- Provide address and contact details
- Offer a summary of available services
- If multiple locations are returned, briefly mention alternatives
- If no locations are found, suggest expanding the search area

For Hospital Location Responses (especially Mitra Keluarga):
- Begin with the name of the closest hospital
- Clearly state how far away it is (in km or minutes)
- Provide the complete address
- Include contact information (phone, website if available)
- List any available services or specialties
- If there are multiple hospitals, mention the next closest options
- Offer to provide directions or additional information if needed
- NEVER invent or make up hospital data - only use information returned from the API
- If no hospital data is available, acknowledge this and suggest alternatives

For Geocoding Responses:
- Never show raw geocoding data to users
- Instead, confirm you've located their area (e.g., "I've found your location in Central Jakarta")
- Then proceed to provide the relevant hospital information
- If the system couldn't find any hospitals near their location, be honest about this - don't make up fictional hospitals

For multi-step reasoning:
- Present your conclusion first
- Explain your reasoning process if helpful
- Break down complex information into digestible parts

STYLING CONVENTIONS:
- Use **bold** for emphasis on important information
- Use bullet points for lists
- Use numbered lists for sequential steps
- Use paragraphs to separate different topics
- When presenting location information, structure clearly:
  * Name of location
  * Address
  * Distance
  * Contact information
  * Available services

SAMPLE RESPONSE FOR HOSPITAL LOCATION:
"The nearest Mitra Keluarga hospital to your location is **Mitra Keluarga Kemang**, which is approximately 2.3 km away (about 10 minutes by car).

Here are the details:
• **Address**: Jl. Kemang Raya No. 35, South Jakarta
• **Phone**: (021) 7194567
• **Services**: Emergency, Maternity, General Practice, Laboratory

The next closest option is Mitra Keluarga Tebet, which is 4.1 km away.

Would you like directions to the hospital or information about specific services available at Mitra Keluarga Kemang?"

Always prioritize being helpful, accurate, and conversational in your responses.`;

  // State for prompts
  const [primaryPrompt, setPrimaryPrompt] = useState({
    model: 'gpt-4o',
    systemPrompt: defaultDecisionMakerPrompt,
    temperature: 0.7,
    maxTokens: 1024
  });
  
  const [secondaryPrompt, setSecondaryPrompt] = useState({
    model: 'gpt-4o',
    systemPrompt: defaultResponseGeneratorPrompt,
    temperature: 0.7,
    maxTokens: 1024
  });
  
  const [chatContext, setChatContext] = useState({
    greeting: 'Hello! I am {{CHATBOT_NAME}}. How can I assist you today?',
    helpMessage: 'You can ask me anything, and I will do my best to assist you.',
    sessionStartMessage: 'Hello {{USER_NAME}}, welcome back! How can I help you today?',
    timeoutMessage: 'Is there anything else I can assist you with?',
    endConversationMessage: 'Thank you for chatting with me today. Have a great day!'
  });

  // Add new state for the OpenAI API Key
  const [openAIConfig, setOpenAIConfig] = useState({
    apiKey: '',
    organization: ''
  });
  
  // Add new state for DeepSeek API Key
  const [deepseekConfig, setDeepseekConfig] = useState({
    apiKey: ''
  });

  // Add new state for AI/ML API Key
  const [aimlapiConfig, setAimlapiConfig] = useState({
    apiKey: ''
  });
  
  // Add simulation selection state
  const [showSimulationSelector, setShowSimulationSelector] = useState(false);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  
  // Add new state for API key warning
  const [showApiKeyWarning, setShowApiKeyWarning] = useState(false);

  // State for saving
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  
  // Load project data when available
  useEffect(() => {
    if (projectData?.data?.project) {
      const project = projectData.data.project;
      
      if (project.primaryLLM) {
        setPrimaryPrompt({
          systemPrompt: project.primaryLLM.systemPrompt || defaultDecisionMakerPrompt,
          model: project.primaryLLM.model || 'gpt-4o',
          temperature: typeof project.primaryLLM.temperature === 'number' ? project.primaryLLM.temperature : 0.7,
          maxTokens: typeof project.primaryLLM.maxTokens === 'number' ? project.primaryLLM.maxTokens : 1024
        });
      }
      
      if (project.secondaryLLM) {
        setSecondaryPrompt({
          systemPrompt: project.secondaryLLM.systemPrompt || defaultResponseGeneratorPrompt,
          model: project.secondaryLLM.model || 'gpt-4o',
          temperature: typeof project.secondaryLLM.temperature === 'number' ? project.secondaryLLM.temperature : 0.7,
          maxTokens: typeof project.secondaryLLM.maxTokens === 'number' ? project.secondaryLLM.maxTokens : 1024
        });
      }
      
      if (project.chatContext) {
        setChatContext({
          messageCount: project.chatContext.messageCount || 5,
          includeSystemMessages: project.chatContext.includeSystemMessages || false
        });
      }
      
      if (project.openAIConfig) {
        setOpenAIConfig({
          apiKey: project.openAIConfig.apiKey || '',
          organization: project.openAIConfig.organization || ''
        });
      }
      
          if (project.deepseekConfig) {
      setDeepseekConfig({
        apiKey: project.deepseekConfig.apiKey || ''
      });
    }

    if (project.aimlapiConfig) {
      setAimlapiConfig({
        apiKey: project.aimlapiConfig.apiKey || ''
      });
    }
    
    // If no API keys are set, force the API Keys tab to be active
    if (!project.openAIConfig?.apiKey && !project.deepseekConfig?.apiKey && !project.aimlapiConfig?.apiKey) {
      setActiveTab('apiKeys');
    }
    
    // Check if API keys are configured
    const hasOpenAIKey = !!project.openAIConfig?.apiKey;
    const hasDeepseekKey = !!project.deepseekConfig?.apiKey;
    const hasAimlapiKey = !!project.aimlapiConfig?.apiKey;
    setShowApiKeyWarning(!hasOpenAIKey && !hasDeepseekKey && !hasAimlapiKey);
    }
  }, [projectData]);
  
  // Determine if any API key is set (OpenAI, DeepSeek, or AI/ML API)
  const apiKeyIsSet = (openAIConfig.apiKey && openAIConfig.apiKey.trim() !== '') || 
                      (deepseekConfig.apiKey && deepseekConfig.apiKey.trim() !== '') ||
                      (aimlapiConfig.apiKey && aimlapiConfig.apiKey.trim() !== '');
  
  const handlePrimaryPromptChange = (e) => {
    const { name, value } = e.target;
    setPrimaryPrompt(prev => ({
      ...prev,
      [name]: name === 'temperature' || name === 'maxTokens' ? Number(value) : value
    }));
  };
  
  const handleSecondaryPromptChange = (e) => {
    const { name, value } = e.target;
    setSecondaryPrompt(prev => ({
      ...prev,
      [name]: name === 'temperature' || name === 'maxTokens' ? Number(value) : value
    }));
  };
  
  const handleChatContextChange = (e) => {
    const { name, value, type, checked } = e.target;
    setChatContext(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) : value
    }));
  };
  
  const handleOpenAIConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOpenAIConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleDeepseekConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeepseekConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAimlapiConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAimlapiConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    
    try {
      // Log what we're about to save for debugging
      console.log('Saving project configuration with API keys:', {
        hasOpenAIKey: openAIConfig.apiKey ? 'Yes (length: ' + openAIConfig.apiKey.length + ')' : 'No',
        hasDeepseekKey: deepseekConfig.apiKey ? 'Yes (length: ' + deepseekConfig.apiKey.length + ')' : 'No',
        hasAimlapiKey: aimlapiConfig.apiKey ? 'Yes (length: ' + aimlapiConfig.apiKey.length + ')' : 'No'
      });
      
      const updateData = {
        primaryLLM: primaryPrompt,
        secondaryLLM: secondaryPrompt,
        chatContext: chatContext,
        openAIConfig: openAIConfig,
        deepseekConfig: deepseekConfig,
        aimlapiConfig: aimlapiConfig
      };
      
      const result = await updateProjectMutation.mutateAsync({
        id: projectId,
        data: updateData
      });
      
      console.log('Project update result:', result);
      
      // Show success toast
      toast.success('Changes saved successfully!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });

    } catch (error) {
      console.error('Error saving prompts:', error);
      setSaveError('Failed to save changes. Please try again.');
      
      toast.error(`Failed to save configuration: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } finally {
      setIsSaving(false);
    } 
  };
  
  const handleAIAssist = (promptType) => {
    setActivePromptType(promptType);
    setIsDialogOpen(true);
    setIsReviewMode(false);
    setGeneratedPrompt('');
    setPromptDescription('');
    setPromptGenerationMode('new'); // Default to creating a new prompt
    
    // Reset model selection to first available model
    if (availableModels.length > 0) {
      setSelectedModel(availableModels[0].id);
    } else {
      setSelectedModel('');
    }
  };
  
  // Function to generate a custom prompt
  const generateCustomPrompt = async () => {
    if (!promptDescription.trim()) return;
    
    setIsGenerating(true);
    setDiffData(null);
    setShowDiffView(false);
    setShowRawResponse(false);
    setRawResponse('');
    setGeneratedPrompt('');
    setApiAnalysis(null);
    setHasGenerationError(false);
    
    try {
      const data = {
        promptType: activePromptType,
        promptDescription: promptDescription,
        mode: promptGenerationMode,
        model: selectedModel
      };
      
      // Add current prompt if we're in modify mode
      if (promptGenerationMode === 'modify') {
        data.currentPrompt = activePromptType === 'primary' 
          ? primaryPrompt.systemPrompt 
          : secondaryPrompt.systemPrompt;
      }
      
      // Add simulation session ID if selected
      if (selectedSimulationId) {
        data.simulationSessionId = selectedSimulationId;
      }
      
      console.log('Sending data to generate prompt:', {
        promptType: data.promptType,
        mode: data.mode,
        model: data.model,
        descriptionLength: data.promptDescription.length,
        currentPromptLength: data.currentPrompt ? data.currentPrompt.length : 0,
        simulationSessionId: data.simulationSessionId || 'none'
      });
      
      // Call the API to generate the prompt
      const response = await apiClient.post(`/projects/${projectId}/prompts/generate`, data);
      console.log('API response:', response);
      
      // Use the debug function to log the full response structure
      debugObjectStructure(response, 'API response');
      
      // Check for successful response
      if (response.status === 'success' || response.data?.status === 'success') {
        const responseData = response.data || response;
        
        if (promptGenerationMode === 'modify') {
          // For modification mode, get diff data and update the prompt
          // Need to handle both possible structures from the API response
          let diffData;
          
          // Check if diffData is directly in responseData or nested inside diffData property
          if (responseData.diffData && 
              typeof responseData.diffData === 'object' && 
              responseData.diffData.additions !== undefined) {
            // Direct structure - data is right in responseData.diffData
            diffData = responseData.diffData;
          } else if (responseData.diffData && 
                    typeof responseData.diffData === 'object' && 
                    responseData.diffData.diffData && 
                    typeof responseData.diffData.diffData === 'object') {
            // Nested structure - data is in responseData.diffData.diffData
            diffData = responseData.diffData.diffData;
          } else {
            // Fallback to empty object if no valid structure found
            diffData = {};
          }
          
          console.log('Diff data received (processed):', {
            structure: diffData === responseData.diffData ? 'direct' : 'nested',
            additions: diffData.additions?.length || 0,
            deletions: diffData.deletions?.length || 0,
            replacements: diffData.replacements?.length || 0
          });
          
          // Get any warnings from the backend
          const warnings = responseData?.warnings || [];
          const hasWarnings = warnings.length > 0;
          console.log('Warnings:', warnings);
          
          // Get raw response if it exists
          const rawResponseData = responseData?.rawResponse || '';
          console.log('Raw response available:', !!rawResponseData);
          if (rawResponseData) {
            setRawResponse(rawResponseData);
            
            // Check if the raw response contains any modification tags
            const hasTags = rawResponseData.includes('[ADD:') || 
                           rawResponseData.includes('[REMOVE:') || 
                           rawResponseData.includes('[REPLACE:');
            console.log('Raw response has tags:', hasTags ? 'YES' : 'NO');
          }
          
          // Set the generated prompt
          const generatedPromptText = responseData?.prompt || '';
          setGeneratedPrompt(generatedPromptText);
          
          // Set diff data if available
          if (Object.keys(diffData).length > 0) {
            setDiffData(diffData);
          }
          
          // Check if we have valid changes to display - look directly at diffData contents
          const hasAdditions = Array.isArray(diffData.additions) && diffData.additions.length > 0;
          const hasDeletions = Array.isArray(diffData.deletions) && diffData.deletions.length > 0;
          const hasReplacements = Array.isArray(diffData.replacements) && diffData.replacements.length > 0;
          
          // Either use calculated value or fall back to responseData.hasChanges
          const hasChanges = hasAdditions || hasDeletions || hasReplacements || responseData?.hasChanges;
          
          console.log('Has changes (calculated):', {
            fromDiffData: hasAdditions || hasDeletions || hasReplacements,
            fromFlags: responseData?.hasChanges,
            final: hasChanges,
            additions: diffData.additions?.length || 0,
            deletions: diffData.deletions?.length || 0,
            replacements: diffData.replacements?.length || 0
          });
          
          // Check if we have tags but couldn't apply any changes
          const hasTagsButNoChanges = responseData?.hasTagsButNoChanges || false;
          console.log('Has tags but no changes:', hasTagsButNoChanges);
          
          // Check for API analysis data
          if (responseData?.apiAnalysis) {
            setApiAnalysis(responseData.apiAnalysis);
          }
          
          // Show warnings or raw response view as appropriate
          if (hasWarnings) {
            console.log('Has warnings, showing warnings');
            // Show warnings notification - use simple string instead of JSX
            let warningMessage = "Warning: Some issues with applying changes. ";
            if (warnings.length > 0) {
              warningMessage += warnings.join(" ");
            }
            warningMessage += " You may want to click \"Show Raw Changes\" to see the original AI suggestion.";
            
            toast.warning(warningMessage, {
              position: "top-right",
              autoClose: 8000
            });
          }
          
          // When to show raw response view: either explicitly requested or we have tags
          // but couldn't apply changes
          if (rawResponseData && hasTagsButNoChanges) {
            console.log('Has tags but no changes, showing raw response view');
            setShowRawResponse(true);
          } else if (hasChanges) {
            // If successful changes, show diff view
            console.log('Showing diff view due to successful changes');
            setShowDiffView(true);
          }
          
          // If there are no changes, but we have diffData, check the diffData more carefully
          if (!hasChanges) {
            // Check if we have actual changes in the diffData
            const hasDiffChanges = diffData && (
              (diffData.additions && diffData.additions.length > 0) ||
              (diffData.deletions && diffData.deletions.length > 0) ||
              (diffData.replacements && diffData.replacements.length > 0)
            );
            
            // Enhanced check - inspect the diffData object directly, bypassing our derived flags
            const showDiffAnyway = diffData && diffData.hasChanges === true;
            
            console.log('Final hasChanges determination:', {
              hasDiffChanges,
              diffDataHasChanges: diffData?.hasChanges,
              showDiffAnyway
            });
            
            if (showDiffAnyway || hasDiffChanges) {
              // Show diff view if the data indicates changes exist
              console.log('Found changes in diffData, showing diff view');
              setShowDiffView(true);
            } else if (!hasWarnings && !rawResponseData.includes('[')) {
              // Only show the "no differences" message if there are truly no changes
              toast.info('No differences detected between the original and generated prompt.', {
                position: "top-right",
                autoClose: 5000
              });
            }
          }
        } else {
          // For new prompt mode, set the generated prompt directly
          setGeneratedPrompt(responseData?.prompt || '');
          
          // Check for API analysis data
          if (responseData?.apiAnalysis) {
            setApiAnalysis(responseData.apiAnalysis);
          }
        }
        
        // Update the state to show the review mode
        setIsReviewMode(true);
      } else {
        // Handle error response
        const errorMessage = response.data?.message || 'Failed to generate prompt. Please try again.';
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000
        });
        
        setHasGenerationError(true);
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      toast.error('Error generating prompt. Please try again.', {
        position: "top-right",
        autoClose: 5000
      });
      
      setHasGenerationError(true);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Function to apply the generated prompt
  const applyGeneratedPrompt = async () => {
    if (!generatedPrompt) {
      toast.error('No prompt to apply.');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Get the current project data
      const project = projectData?.data?.project;
      
      if (!project) {
        toast.error('Project data not available.');
        setIsSaving(false);
        return;
      }
      
      // Prepare updated prompt data
      const updatedProject = { ...project };
      
      if (activePromptType === 'primary') {
        // Update primary prompt
        updatedProject.primaryLLM = {
          ...updatedProject.primaryLLM,
          systemPrompt: generatedPrompt
        };
      } else {
        // Update secondary prompt
        updatedProject.secondaryLLM = {
          ...updatedProject.secondaryLLM,
          systemPrompt: generatedPrompt
        };
      }
      
      // Update project
      await updateProjectMutation.mutateAsync({
        id: projectId,
        data: updatedProject
      });
      
      toast.success(`${activePromptType === 'primary' ? 'Decision maker' : 'Response generator'} prompt updated successfully!`);
      
      // Reset states
      setIsReviewMode(false);
      setGeneratedPrompt('');
      setShowDiffView(false);
      setPromptDescription('');
      setApiAnalysis(null);
      setHasGenerationError(false);
      setIsDialogOpen(false);
      
      // Update local state to reflect changes
      if (activePromptType === 'primary') {
        setPrimaryPrompt(prev => ({
          ...prev,
          systemPrompt: generatedPrompt
        }));
      } else {
        setSecondaryPrompt(prev => ({
          ...prev,
          systemPrompt: generatedPrompt
        }));
      }
      
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save the prompt. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Function to handle closing the dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setIsReviewMode(false);
    setPromptDescription('');
    setHasGenerationError(false); // Reset error state
    setDiffData(null); // Reset diff data
    setShowDiffView(false); // Reset diff view state
    setShowRawResponse(false); // Reset raw response view state
    setRawResponse(''); // Clear raw response data
  };
  
  // Add this function to the component
  const scrollToSection = (sectionId) => {
    if (sectionId === 'api-keys') {
      setActiveTab('apiKeys');
    }
    
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  const handleForceRefresh = () => {
    // Force a full page reload to clear all caches
    window.location.reload(true);
  };
  
  // Add model selection handler
  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
  };
  
  /**
   * Helper function to safely debug complex nested objects
   */
  const debugObjectStructure = (obj, name = 'object') => {
    try {
      // Only run this in development
      if (process.env.NODE_ENV === 'development') {
        // First determine if we're looking at the raw API response or the extracted responseData
        const responseObj = obj?.data?.status ? obj.data : obj;
        
        console.log(`Debug ${name} structure:`, {
          type: typeof responseObj,
          isNull: responseObj === null,
          isArray: Array.isArray(responseObj),
          keys: responseObj ? Object.keys(responseObj) : [],
          status: responseObj?.status,
          diffData: responseObj?.diffData ? {
            type: typeof responseObj.diffData,
            isEmpty: Object.keys(responseObj.diffData).length === 0,
            keys: Object.keys(responseObj.diffData),
            additionsLength: responseObj.diffData.additions?.length,
            deletionsLength: responseObj.diffData.deletions?.length,
            replacementsLength: responseObj.diffData.replacements?.length,
          } : 'No diffData',
          hasRawResponse: responseObj?.rawResponse ? true : false,
          rawResponseLength: responseObj?.rawResponse?.length || 0,
          hasWarnings: responseObj?.warnings?.length > 0,
          hasChanges: responseObj?.hasChanges,
          hasTagsButNoChanges: responseObj?.hasTagsButNoChanges,
        });
      }
    } catch (error) {
      console.error('Error debugging object:', error);
    }
  };
  
  /**
   * Process raw changes by removing the tagging and applying the changes
   * @param {string} rawText - The raw text with [ADD:], [REMOVE:], [REPLACE:] tags
   * @returns {string} - The processed text with changes applied
   */
  const processRawChanges = (rawText) => {
    if (!rawText) return '';
    
    console.log('Processing raw changes...');
    
    // First check if we're dealing with a tagged response or a complete system prompt
    // If it's already a clean prompt with no tags, return it as is
    if (!rawText.includes('[ADD:') && !rawText.includes('[REMOVE:') && !rawText.includes('[REPLACE:')) {
      console.log('No tags found, treating as a complete prompt');
      return rawText;
    }
    
    console.log('Found modification tags, processing them...');
    
    // Extract any preamble text before the first tag
    let preamble = '';
    const firstTagIndex = Math.min(
      rawText.indexOf('[ADD:') !== -1 ? rawText.indexOf('[ADD:') : Infinity,
      rawText.indexOf('[REMOVE:') !== -1 ? rawText.indexOf('[REMOVE:') : Infinity,
      rawText.indexOf('[REPLACE:') !== -1 ? rawText.indexOf('[REPLACE:') : Infinity
    );

    if (firstTagIndex > 0 && firstTagIndex !== Infinity) {
      preamble = rawText.substring(0, firstTagIndex).trim();
      console.log('Found preamble:', preamble.substring(0, 50) + (preamble.length > 50 ? '...' : ''));
    }

    // Extract any conclusion text after the last tag
    let conclusion = '';
    const lastTagEndIndex = Math.max(
      rawText.lastIndexOf(']'),
      0
    );

    if (lastTagEndIndex < rawText.length - 1) {
      conclusion = rawText.substring(lastTagEndIndex + 1).trim();
      console.log('Found conclusion:', conclusion.substring(0, 50) + (conclusion.length > 50 ? '...' : ''));
    }
    
    let processedText = rawText;
    
    // Count tags before processing
    const addTagsBefore = (processedText.match(/\[ADD:/g) || []).length;
    const removeTagsBefore = (processedText.match(/\[REMOVE:/g) || []).length;
    const replaceTagsBefore = (processedText.match(/\[REPLACE:/g) || []).length;
    
    console.log(`Found ${addTagsBefore} ADD tags, ${removeTagsBefore} REMOVE tags, and ${replaceTagsBefore} REPLACE tags`);
    
    // Enhanced regex patterns with better handling for complex content
    // Process [REMOVE:] tags - remove the tag and the content
    processedText = processedText.replace(/\[REMOVE:([\s\S]*?)(?=\])\]/gs, '');
    
    // Process [REPLACE:...WITH:...] tags - replace with just the new content
    // Improved regex with non-greedy matching and better WITH: detection
    processedText = processedText.replace(/\[REPLACE:([\s\S]*?)WITH:([\s\S]*?)(?=\])\]/gs, function(match, p1, p2) {
      // p1 is the content to replace, p2 is the replacement
      return p2.trim();
    });
    
    // Process [ADD:] tags - remove the tags, keep content
    processedText = processedText.replace(/\[ADD:([\s\S]*?)(?=\])\]/gs, function(match, p1) {
      // p1 is the content to add
      return p1.trim();
    });

    // Remove any remaining potentially malformed tags
    processedText = processedText.replace(/\[(ADD|REMOVE|REPLACE):([\s\S]*?)(?=\])\]/gs, '');
    processedText = processedText.replace(/\[WITH:([\s\S]*?)(?=\])\]/gs, '');
    
    // Clean up extra whitespace that might have been created
    processedText = processedText.replace(/\n\s*\n\s*\n/g, '\n\n');
    
    // Remove any trailing or leading whitespace
    processedText = processedText.trim();
    
    // If the processed text is very short or empty, something went wrong
    if (processedText.length < 50) {
      console.log('Warning: Processed text is very short after removing tags:', processedText);
      
      // As a fallback, try a different approach for complex nested tags
      try {
        let fallbackText = rawText;
        
        // First, extract all complete tag blocks
        const tagBlocks = [];
        const tagPattern = /\[(ADD|REMOVE|REPLACE):([\s\S]*?)(?=\])\]/gs;
        let match;
        
        while ((match = tagPattern.exec(rawText)) !== null) {
          const fullMatch = match[0];
          const tagType = match[1];
          const content = match[2].trim();
          
          // For REPLACE tags, we need to handle the WITH: part
          if (tagType === 'REPLACE') {
            const withParts = content.split('WITH:');
            if (withParts.length === 2) {
              tagBlocks.push({
                type: 'REPLACE',
                oldText: withParts[0].trim(),
                newText: withParts[1].trim(),
                fullMatch
              });
            } else {
              // Malformed REPLACE tag
              tagBlocks.push({
                type: 'ADD',
                content: content,
                fullMatch
              });
            }
          } else if (tagType === 'ADD') {
            tagBlocks.push({
              type: 'ADD',
              content: content,
              fullMatch
            });
          } else if (tagType === 'REMOVE') {
            // For REMOVE, we'll just remove the tag but keep the content for reference
            tagBlocks.push({
              type: 'REMOVE',
              content: content,
              fullMatch
            });
          }
        }
        
        // Now process each tag block
        for (const block of tagBlocks) {
          if (block.type === 'REPLACE') {
            // Replace the entire tag with just the new content
            fallbackText = fallbackText.replace(block.fullMatch, block.newText);
          } else if (block.type === 'ADD') {
            // Replace the tag with just the content
            fallbackText = fallbackText.replace(block.fullMatch, block.content);
          } else if (block.type === 'REMOVE') {
            // Replace the tag with nothing (remove it)
            fallbackText = fallbackText.replace(block.fullMatch, '');
          }
        }
        
        // Clean up excess whitespace
        fallbackText = fallbackText.replace(/\n\s*\n\s*\n/g, '\n\n').trim();
        
        console.log('Alternative processing approach result length:', fallbackText.length);
        
        // If the fallback approach resulted in more content, use that
        if (fallbackText.length > processedText.length) {
          processedText = fallbackText;
        }
      } catch (error) {
        console.error('Error in fallback tag processing:', error);
        
        // Last resort: just strip out all tag markers
        processedText = rawText
          .replace(/\[ADD:/g, '')
          .replace(/\[REMOVE:/g, '')
          .replace(/\[REPLACE:/g, '')
          .replace(/WITH:/g, '')
          .replace(/\]/g, '')
          .trim();
      }
      
      console.log('Fallback processed text length:', processedText.length);
    }
    
    // Fix ordering issues with special sections
    try {
      // Generic approach for proper section ordering without hard-coding specific section names
      // Identify sections that might need special ordering treatment
      const sectionMatches = [];
      const sectionPattern = /\n([A-Z][A-Z\s_]+)(?:\n|\s*:)/g;
      let sectionMatch;
      
      // Collect all sections and their positions
      while ((sectionMatch = sectionPattern.exec(processedText)) !== null) {
        const sectionName = sectionMatch[1].trim();
        const sectionPos = sectionMatch.index;
        
        // Find the potential end of this section (next section start or end of text)
        let endPos = processedText.length;
        const nextSectionMatch = processedText.slice(sectionPos + sectionName.length + 1).search(/\n[A-Z][A-Z\s_]+(?:\n|\s*:)/);
        if (nextSectionMatch !== -1) {
          endPos = sectionPos + sectionName.length + 1 + nextSectionMatch;
        }
        
        // Store info about this section
        sectionMatches.push({
          name: sectionName,
          start: sectionPos,
          end: endPos,
          content: processedText.substring(sectionPos, endPos),
          // Check if this section contains example responses (likely belongs at the end)
          isExample: sectionName.includes('EXAMPLE') && sectionName.includes('RESPONSE'),
          // Check if this is a rules section (should appear before examples)
          isRules: sectionName.includes('RULES') || sectionName.includes('WORKFLOW')
        });
      }
      
      // If we have multiple sections and at least one example response section, reorder them
      if (sectionMatches.length > 1 && sectionMatches.some(s => s.isExample)) {
        // Find all example response sections that should be moved to the end
        const exampleSections = sectionMatches.filter(s => s.isExample);
        const otherSections = sectionMatches.filter(s => !s.isExample);
        
        // Check if we need to reorder (examples should be after rules sections)
        const needsReordering = exampleSections.some(example => {
          return otherSections.some(other => other.isRules && example.start < other.start);
        });
        
        if (needsReordering) {
          console.log('Reordering sections to place example responses at the end');
          
          // Remove all example sections
          let newText = processedText;
          for (const section of exampleSections) {
            newText = newText.replace(section.content, '');
          }
          
          // Add example sections at the end
          for (const section of exampleSections) {
            newText = newText.trim() + '\n\n' + section.content.trim();
          }
          
          // Clean up any double newlines created during the process
          processedText = newText.replace(/\n{3,}/g, '\n\n').trim();
        }
      }
    } catch (error) {
      console.error('Error fixing section ordering:', error);
      // Continue with the original processed text if there's an error
    }
    
    // If there's meaningful preamble or conclusion text (explanation from the AI), 
    // preserve it in a comment format at the top/bottom
    let finalText = '';
    if (preamble && preamble.length > 20) {
      finalText += `// PREAMBLE (AI EXPLANATION): \n// ${preamble.split('\n').join('\n// ')}\n\n`;
    }
    
    finalText += processedText;
    
    if (conclusion && conclusion.length > 20) {
      finalText += `\n\n// CONCLUSION (AI EXPLANATION): \n// ${conclusion.split('\n').join('\n// ')}`;
    }
    
    console.log('Successfully processed raw changes, new length:', finalText.length);
    return finalText;
  };
  
  /**
   * Apply the raw changes from the AI, processing the tags to create a clean version
   */
  const applyRawChanges = () => {
    try {
      if (!rawResponse) {
        toast.error('No raw changes available to apply');
        return;
      }
      
      console.log('Applying raw changes...');
      
      // Process the raw response to remove tags
      const processedPrompt = processRawChanges(rawResponse);
      
      if (!processedPrompt || processedPrompt.trim() === '') {
        toast.error('Error processing raw changes - resulted in empty content');
        return;
      }
      
      // Update the generatedPrompt state with the processed content
      setGeneratedPrompt(processedPrompt);
      
      // Hide the raw view and show the final result
      setShowRawResponse(false);
      
      // Count the number of changes applied by looking for tag patterns in the original
      const addCount = (rawResponse.match(/\[ADD:/g) || []).length;
      const removeCount = (rawResponse.match(/\[REMOVE:/g) || []).length;
      const replaceCount = (rawResponse.match(/\[REPLACE:/g) || []).length;
      const totalChanges = addCount + removeCount + replaceCount;
      
      // Show success notification with details about the changes
      toast.success(
        `Raw changes applied successfully! Applied ${totalChanges} changes: ${addCount} additions, ${removeCount} removals, ${replaceCount} replacements.`, 
        {
          position: "top-right",
          autoClose: 8000
        }
      );
      
      // Show additional info toast about the next steps
      setTimeout(() => {
        toast.info(
          `Next Steps: 1) Review the prompt carefully 2) Make any manual adjustments if needed 3) Click "Apply to Project" when ready`,
          {
            position: "top-right",
            autoClose: 10000
          }
        );
      }, 1000);
      
    } catch (error) {
      console.error('Error applying raw changes:', error);
      toast.error('Error applying raw changes. Please try again or copy the content manually.');
    }
  };
  
  // Add query for fetching recent chat sessions
  const { data: recentSessions, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['recentChatSessions', projectId],
    queryFn: async () => {
      if (!projectId || !showSimulationSelector) return [];
      const response = await apiClient.get(`/projects/${projectId}/chat-sessions?limit=10`);

      console.log("Chat Session Response: ", response);

      return response.data || [];
    },
    enabled: !!projectId && showSimulationSelector,
  });
  
  if (isLoading) {
    return (
      <Layout>
        <div className="dashboard">
          <h1 className="page-title">Prompt Builder</h1>
          <div className="card p-4 text-center mt-4">
            <p>Loading project data...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (error) {
    return (
      <Layout>
        <div className="dashboard">
          <h1 className="page-title">Prompt Builder</h1>
          <div className="card p-4 mt-4">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="text-center"><strong>Error loading project data.</strong></p>
              <p className="mt-2">Error details: {error.message}</p>
              <div className="flex justify-center mt-4">
                <button 
                  className="btn btn-primary"
                  onClick={() => refetch()}
                >
                  Try Again
                </button>
              </div>
            </div>
            <div className="mt-4 text-center text-gray-500">
              <p>You can try refreshing the page or contact support if this error persists.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="dashboard">
        <div className="flex justify-between items-center mb-4">
          <h1 className="page-title">Prompt Builder</h1>
          <button
            type="button"
            className="btn btn-secondary flex items-center text-smd"
            onClick={handleForceRefresh}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 mr-1" 
              viewBox="0 0 20 20" 
              fill="currentColor">
              <path 
                fillRule="evenodd" 
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" 
                clipRule="evenodd" 
              />
            </svg>
            Refresh
          </button>
        </div>
        
        {showApiKeyWarning && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex justify-between w-full">
                <p className="text-sm text-yellow-700">
                  <strong>No LLM API keys configured.</strong> The AI Assistant will use a template-based approach instead of generated prompts.{' '}
                  <a href="#" className="font-medium underline text-yellow-700 hover:text-yellow-600" onClick={() => scrollToSection('api-keys')}>
                    Add API keys
                  </a>
                </p>
                <button 
                  onClick={handleForceRefresh}
                  className="text-xs text-yellow-700 hover:text-yellow-800 underline"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="card mt-4 p-4">
          <div className="flex border-b mb-4">
            {apiKeyIsSet && (
              <>
                <button
                  className={`py-2 px-4 mr-2 text-smd tabButton ${activeTab === 'primary' ? 'border-b-2 border-primary-500 text-primary-600 font-medium selectedTab' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('primary')}
                >
                  First LLM (Decision Making)
                </button>
                <button
                  className={`py-2 px-4 mr-2 text-smd tabButton ${activeTab === 'secondary' ? 'border-b-2 border-primary-500 text-primary-600 font-medium selectedTab' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('secondary')}
                >
                  Second LLM (Response Generation)
                </button>
                <button
                  className={`py-2 px-4 mr-2 text-smd tabButton ${activeTab === 'context' ? 'border-b-2 border-primary-500 text-primary-600 font-medium selectedTab' : 'text-gray-500'}`}
                  onClick={() => setActiveTab('context')}
                >
                  Chat Context
                </button>
              </>
            )}
            <button
              className={`py-2 px-4 text-smd tabButton ${activeTab === 'apiKeys' ? 'border-b-2 border-primary-500 text-primary-600 font-medium selectedTab' : 'text-gray-500'}`}
              onClick={() => setActiveTab('apiKeys')}
            >
              API Keys
            </button>
          </div>
          
          {activeTab === 'primary' && apiKeyIsSet ? (
            <div>
              <h2 className="text-xl font-medium mb-4">First LLM Configuration (Decision Maker)</h2>
              <p className="mb-4">This LLM analyzes user input and decides what action to take next. It outputs structured JSON that the system can process.</p>
              
              <div className="form-group mb-4">
                <label htmlFor="primary-model" className="form-label">
                  Model
                </label>
                <select
                  id="primary-model"
                  name="model"
                  className="form-input"
                  value={primaryPrompt.model}
                  onChange={handlePrimaryPromptChange}
                >
                  {(openAIConfig.apiKey || aimlapiConfig.apiKey) && (
                    <>
                      {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </>
                  )}
                  {(deepseekConfig.apiKey || aimlapiConfig.apiKey) && (
                    <>
                      <option value="deepseek-chat">DeepSeek-V3</option>
                    </>
                  )}
                  {aimlapiConfig.apiKey && (
                    <>
                      <optgroup label="Qwen Models">
                        <option value="qwen-max">Qwen Max</option>
                        <option value="qwen-plus">Qwen Plus</option>
                        <option value="qwen-turbo">Qwen Turbo</option>
                      </optgroup>
                      <optgroup label="Claude Models">
                        {CLAUDE_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini Models">
                        {GEMINI_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemma Models">
                        <option value="gemma-2">Gemma 2</option>
                        <option value="gemma-3">Gemma 3</option>
                      </optgroup>
                      <optgroup label="Meta Llama Models">
                        <option value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</option>
                        <option value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</option>
                        <option value="meta-llama/llama-4-scout">Llama 4 Scout</option>
                        <option value="meta-llama/llama-4-maverick">Llama 4 Maverick</option>
                      </optgroup>
                      <optgroup label="NVIDIA Models">
                        <option value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Llama 3.1 Nemotron 70B Instruct</option>
                      </optgroup>
                    </>
                  )}
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label htmlFor="primary-temperature" className="form-label">
                    Temperature: {primaryPrompt.temperature}
                  </label>
                  <input
                    type="range"
                    id="primary-temperature"
                    name="temperature"
                    min="0"
                    max="2"
                    step="0.1"
                    className="form-input"
                    value={primaryPrompt.temperature}
                    onChange={handlePrimaryPromptChange}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>More Precise</span>
                    <span>More Creative</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="primary-maxTokens" className="form-label">
                    Max Tokens: {primaryPrompt.maxTokens}
                  </label>
                  <input
                    type="range"
                    id="primary-maxTokens"
                    name="maxTokens"
                    min="256"
                    max="4096"
                    step="256"
                    className="form-input"
                    value={primaryPrompt.maxTokens}
                    onChange={handlePrimaryPromptChange}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Shorter</span>
                    <span>Longer</span>
                  </div>
                </div>
              </div>
              
              <div className="form-group mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="primary-systemPrompt" className="form-label">
                    System Prompt
                  </label>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary mr-2 flex items-center text-smd"
                      onClick={() => handleAIAssist('primary')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      AI Assist
                    </button>
                  </div>
                </div>
                <textarea
                  id="primary-systemPrompt"
                  name="systemPrompt"
                  rows="15"
                  className="form-input font-mono text-sm"
                  value={primaryPrompt.systemPrompt}
                  onChange={handlePrimaryPromptChange}
                  placeholder="Enter system prompt for the first LLM"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  This prompt should instruct the LLM to output structured JSON with intent, action, and parameters.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-primary text-smd"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'secondary' && apiKeyIsSet ? (
            <div>
              <h2 className="text-xl font-medium mb-4">Second LLM Configuration (Response Generator)</h2>
              <p className="mb-4">This LLM generates the final response to the user based on the data retrieved from APIs or the knowledge base.</p>
              
              <div className="form-group mb-4">
                <label htmlFor="secondary-model" className="form-label">
                  Model
                </label>
                <select
                  id="secondary-model"
                  name="model"
                  className="form-input"
                  value={secondaryPrompt.model}
                  onChange={handleSecondaryPromptChange}
                >
                  {(openAIConfig.apiKey || aimlapiConfig.apiKey) && (
                    <>
                      {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                        <option key={model.value} value={model.value}>{model.label}</option>
                      ))}
                    </>
                  )}
                  {(deepseekConfig.apiKey || aimlapiConfig.apiKey) && (
                    <>
                      <option value="deepseek-chat">DeepSeek-V3</option>
                    </>
                  )}
                  {aimlapiConfig.apiKey && (
                    <>
                      <optgroup label="Qwen Models">
                        <option value="qwen-max">Qwen Max</option>
                        <option value="qwen-plus">Qwen Plus</option>
                        <option value="qwen-turbo">Qwen Turbo</option>
                      </optgroup>
                      <optgroup label="Claude Models">
                        {CLAUDE_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemini Models">
                        {GEMINI_MODEL_OPTIONS.map((model) => (
                          <option key={model.value} value={model.value}>{model.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Gemma Models">
                        <option value="gemma-2">Gemma 2</option>
                        <option value="gemma-3">Gemma 3</option>
                      </optgroup>
                      <optgroup label="Meta Llama Models">
                        <option value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</option>
                        <option value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</option>
                        <option value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</option>
                        <option value="meta-llama/llama-4-scout">Llama 4 Scout</option>
                        <option value="meta-llama/llama-4-maverick">Llama 4 Maverick</option>
                      </optgroup>
                      <optgroup label="NVIDIA Models">
                        <option value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Llama 3.1 Nemotron 70B Instruct</option>
                      </optgroup>
                    </>
                  )}
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label htmlFor="secondary-temperature" className="form-label">
                    Temperature: {secondaryPrompt.temperature}
                  </label>
                  <input
                    type="range"
                    id="secondary-temperature"
                    name="temperature"
                    min="0"
                    max="2"
                    step="0.1"
                    className="form-input"
                    value={secondaryPrompt.temperature}
                    onChange={handleSecondaryPromptChange}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>More Precise</span>
                    <span>More Creative</span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="secondary-maxTokens" className="form-label">
                    Max Tokens: {secondaryPrompt.maxTokens}
                  </label>
                  <input
                    type="range"
                    id="secondary-maxTokens"
                    name="maxTokens"
                    min="256"
                    max="4096"
                    step="256"
                    className="form-input"
                    value={secondaryPrompt.maxTokens}
                    onChange={handleSecondaryPromptChange}
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Shorter</span>
                    <span>Longer</span>
                  </div>
                </div>
              </div>
              
              <div className="form-group mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="secondary-systemPrompt" className="form-label">
                    System Prompt
                  </label>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary mr-2 flex items-center text-smd"
                      onClick={() => handleAIAssist('secondary')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      AI Assist
                    </button>
                  </div>
                </div>
                <textarea
                  id="secondary-systemPrompt"
                  name="systemPrompt"
                  rows="15"
                  className="form-input font-mono text-sm"
                  value={secondaryPrompt.systemPrompt}
                  onChange={handleSecondaryPromptChange}
                  placeholder="Enter system prompt for the second LLM"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  This prompt should instruct the LLM to generate natural, conversational responses based on the data provided.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-primary text-smd"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'context' && apiKeyIsSet ? (
            <div>
              <h2 className="text-xl font-medium mb-4">Chat Context Settings</h2>
              <p className="mb-4">Configure how many previous messages are included in the chat context for the LLMs.</p>
              
              <div className="form-group mb-4">
                <label htmlFor="messageCount" className="form-label">
                  Number of Previous Messages: {chatContext.messageCount}
                </label>
                <input
                  type="range"
                  id="messageCount"
                  name="messageCount"
                  min="1"
                  max="80"
                  step="1"
                  className="form-input"
                  value={chatContext.messageCount}
                  onChange={handleChatContextChange}
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Less Context (Faster)</span>
                  <span>More Context (Better Memory)</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Controls how many previous messages are included in the chat context. More messages provide better context but use more tokens.
                </p>
              </div>
              
              <div className="form-group mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeSystemMessages"
                    name="includeSystemMessages"
                    className="form-checkbox"
                    checked={chatContext.includeSystemMessages}
                    onChange={handleChatContextChange}
                  />
                  <label htmlFor="includeSystemMessages" className="form-label ml-2">
                    Include System Messages in Context
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  When enabled, system messages will be included in the chat context. This can help with maintaining consistent behavior but uses more tokens.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <h3 className="text-blue-800 font-medium mb-2">About Chat Context</h3>
                <p className="text-blue-700 text-sm">
                  Chat context refers to the previous messages that are sent to the LLM along with the current message. 
                  This helps the LLM understand the conversation history and provide more relevant responses.
                </p>
                <ul className="list-disc list-inside text-blue-700 text-sm mt-2">
                  <li>More context messages = better memory but higher token usage</li>
                  <li>Fewer context messages = lower token usage but less memory</li>
                  <li>System messages can provide additional guidance but use more tokens</li>
                </ul>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-primary text-smd"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'apiKeys' ? (
            <div id="api-keys" className={`mt-4 ${activeTab === 'apiKeys' ? 'block' : 'hidden'}`}>
              <h2 className="text-xl font-medium mb-4">API Key Configuration</h2>
              
              {!apiKeyIsSet && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
                  <h3 className="text-yellow-800 font-medium mb-2">API Key Required</h3>
                  <p className="text-yellow-700">
                    An OpenAI, DeepSeek, or AI/ML API key is required before you can configure prompts for this project. 
                    Please enter at least one API key below to continue.
                  </p>
                </div>
              )}
              
              <p className="mb-4">Each project requires its own API key for operation. You can use OpenAI, DeepSeek, AI/ML API, or any combination of these services.</p>
              
              {/* OpenAI API Key */}
              <div className="form-group mb-6">
                <label htmlFor="openai-apiKey" className="form-label">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  id="openai-apiKey"
                  name="apiKey"
                  className="form-input"
                  value={openAIConfig.apiKey}
                  onChange={handleOpenAIConfigChange}
                  placeholder="sk-..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your API key is stored securely and never exposed to clients.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline">
                    Get your OpenAI API key here
                  </a>
                </p>
              </div>
              
              {/* DeepSeek API Key */}
              <div className="form-group mb-6">
                <label htmlFor="deepseek-apiKey" className="form-label">
                  DeepSeek API Key
                </label>
                <input
                  type="password"
                  id="deepseek-apiKey"
                  name="apiKey"
                  className="form-input"
                  value={deepseekConfig.apiKey}
                  onChange={handleDeepseekConfigChange}
                  placeholder="sk-..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your API key is stored securely and never exposed to clients.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer" className="underline">
                    Get your DeepSeek API key here
                  </a>
                </p>
              </div>

              {/* AI/ML API Key */}
              <div className="form-group mb-6">
                <label htmlFor="aimlapi-apiKey" className="form-label">
                  AI/ML API Key
                </label>
                <input
                  type="password"
                  id="aimlapi-apiKey"
                  name="apiKey"
                  className="form-input"
                  value={aimlapiConfig.apiKey}
                  onChange={handleAimlapiConfigChange}
                  placeholder="Enter your AI/ML API key..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your API key is stored securely and never exposed to clients.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  <a href="https://aimlapi.com" target="_blank" rel="noopener noreferrer" className="underline">
                    Get your AI/ML API key here
                  </a>
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                <h3 className="text-blue-800 font-medium mb-2">About API Keys</h3>
                <p className="text-blue-700 text-sm">
                  Each project requires at least one API key, which allows:
                </p>
                <ul className="list-disc list-inside text-blue-700 text-sm mt-2">
                  <li>Complete control over your API usage and billing</li>
                  <li>Independent management of rate limits and quotas</li>
                  <li>Enhanced security through isolated credentials</li>
                </ul>
                <p className="text-blue-700 text-sm mt-2">
                  You can use OpenAI, DeepSeek, AI/ML API, or any combination of these services, depending on your requirements.
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn btn-primary text-smd"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* This should never be shown since we're always redirecting to the apiKeys tab when no key is set */}
              <p className="text-center">Please select a tab to continue.</p>
            </div>
          )}
          
          {saveSuccess && (
            <div className="mt-4 p-2 bg-green-100 text-green-700 rounded">
              Changes saved successfully!
            </div>
          )}
          
          {saveError && (
            <div className="mt-4 p-2 bg-red-100 text-red-700 rounded">
              {saveError}
            </div>
          )}
        </div>
        
        {/* AI Assist Dialog */}
        {isDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-medium">
                  {activePromptType === 'primary' 
                    ? 'AI Assist: Decision Maker Prompt' 
                    : 'AI Assist: Response Generator Prompt'}
                </h2>
                
                <button 
                  className="text-gray-500 hover:text-gray-700"
                  onClick={handleCloseDialog}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Error Recovery Section */}
              {hasGenerationError && (
                <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">
                        <strong>Error generating prompt.</strong> Try one of these options:
                      </p>
                      <div className="mt-2 flex space-x-3">
                        <button 
                          className="text-sm font-medium text-red-700 hover:text-red-600"
                          onClick={() => {
                            setHasGenerationError(false);
                            generateCustomPrompt();
                          }}
                        >
                          Try Again
                        </button>
                        <button 
                          className="text-sm font-medium text-red-700 hover:text-red-600"
                          onClick={() => {
                            setHasGenerationError(false);
                            handleCloseDialog();
                          }}
                        >
                          Cancel
                        </button>
                        <button 
                          className="text-sm font-medium text-red-700 hover:text-red-600"
                          onClick={handleForceRefresh}
                        >
                          Refresh Page
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {!isReviewMode ? (
                // Prompt Generation Input Screen
                <>
                  <p className="text-gray-600 mb-4">
                    {activePromptType === 'primary'
                      ? 'Describe what you want your decision maker prompt to accomplish. This LLM analyzes user input and decides what action to take.'
                      : 'Describe what you want your response generator prompt to accomplish. This LLM generates the final response to the user.'}
                  </p>
                  
                  <div className="mb-4">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2 text-smd">
                      <input
                        type="radio"
                        name="promptMode"
                        value="new"
                        checked={promptGenerationMode === 'new'}
                        onChange={() => setPromptGenerationMode('new')}
                        className="mr-2"
                      />
                      Create a new prompt from scratch
                    </label>
                    
                    <label className="flex items-center text-sm font-medium text-gray-700 text-smd">
                      <input
                        type="radio"
                        name="promptMode"
                        value="modify"
                        checked={promptGenerationMode === 'modify'}
                        onChange={() => setPromptGenerationMode('modify')}
                        className="mr-2"
                      />
                      Modify the current prompt
                    </label>
                    
                    {/* Add warning for modify mode when no prompt exists */}
                    {promptGenerationMode === 'modify' && (
                      <div className="mb-4 mt-2">
                        {(activePromptType === 'primary' && (!primaryPrompt.systemPrompt || primaryPrompt.systemPrompt.trim() === '')) || 
                         (activePromptType === 'secondary' && (!secondaryPrompt.systemPrompt || secondaryPrompt.systemPrompt.trim() === '')) ? (
                          <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded border border-amber-300 mt-2">
                            <div className="flex items-start">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              <div>
                                <p className="font-medium">No prompt to modify</p>
                                <p className="mt-1">There is currently no {activePromptType === 'primary' ? 'decision maker' : 'response generator'} prompt to modify. Please select "Create a new prompt" option instead.</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-blue-50 text-blue-800 text-sm p-3 rounded border border-blue-200 mt-2">
                            <div className="flex items-start">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <span>The AI will modify your existing prompt based on your description. Your current prompt has {activePromptType === 'primary' ? primaryPrompt.systemPrompt.length : secondaryPrompt.systemPrompt.length} characters.</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {promptGenerationMode === 'modify' && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <h3 className="text-sm font-medium text-gray-700">Current Prompt Preview:</h3>
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center text-smd"
                          onClick={() => {
                            const preview = document.getElementById('current-prompt-input-preview');
                            if (preview.classList.contains('h-20')) {
                              preview.classList.remove('h-20');
                              preview.classList.add('h-64');
                              document.getElementById('expand-collapse-btn').innerText = 'Show Less';
                            } else {
                              preview.classList.remove('h-64');
                              preview.classList.add('h-20');
                              document.getElementById('expand-collapse-btn').innerText = 'Show More';
                            }
                          }}
                          id="expand-collapse-btn"
                        >
                          Show More
                        </button>
                      </div>
                      <div 
                        id="current-prompt-input-preview" 
                        className="font-mono text-xs bg-gray-50 border border-gray-200 rounded-md p-3 h-20 overflow-y-auto whitespace-pre-wrap transition-all duration-300 leading-relaxed code-highlight"
                      >
                        {activePromptType === 'primary' 
                          ? primaryPrompt.systemPrompt 
                          : secondaryPrompt.systemPrompt}
                      </div>
                    </div>
                  )}
                  
                  <div className="form-group mb-4">
                    <label htmlFor="prompt-description" className="form-label mb-2 block text-smd">
                      {promptGenerationMode === 'new' 
                        ? 'What should this prompt do?' 
                        : 'How should the current prompt be modified?'}
                    </label>
                    <textarea
                      id="prompt-description"
                      className="form-input w-full"
                      rows="6"
                      placeholder={promptGenerationMode === 'new' 
                        ? "E.g., I want a prompt that can handle restaurant bookings, answer product questions, and manage customer service issues..." 
                        : "E.g., Add a section about handling payment processing questions, or improve the examples for location-based queries..."}
                      value={promptDescription}
                      onChange={(e) => setPromptDescription(e.target.value)}
                    ></textarea>
                  </div>

                  {/* Add model selector */}
                  <div className="form-group mb-4">
                    <label htmlFor="model-select" className="form-label mb-2 block text-smd">
                      Select Model for Generation
                    </label>
                    <select
                      id="model-select"
                      value={selectedModel}
                      onChange={handleModelChange}
                      className="form-input w-full"
                      disabled={availableModels.length === 0}
                    >
                      {availableModels.length === 0 ? (
                        <option value="">No models available</option>
                      ) : (
                        availableModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))
                      )}
                    </select>
                    {availableModels.length === 0 && (
                      <p className="mt-2 text-sm text-red-500">
                        Please configure OpenAI, DeepSeek, or AI/ML API keys in the project settings to use AI Assist.
                      </p>
                    )}
                  </div>
                  
                  {/* Simulation logs selector */}
                  <div className="mt-3 mb-4">
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="useSimulationLogs"
                        className="mr-2"
                        checked={showSimulationSelector}
                        onChange={(e) => setShowSimulationSelector(e.target.checked)}
                      />
                      <label htmlFor="useSimulationLogs" className="text-sm font-medium text-gray-700">
                        Include simulation logs as context
                      </label>
                    </div>
                    
                    {showSimulationSelector && (
                      <div className="mt-2">
                        <label htmlFor="simulationId" className="block text-sm font-medium text-gray-700 mb-1">
                          Select a recent simulation
                        </label>
                        <select
                          id="simulationId"
                          value={selectedSimulationId}
                          onChange={(e) => setSelectedSimulationId(e.target.value)}
                          className="form-select block w-full mt-1"
                          disabled={isLoadingSessions}
                        >
                          <option value="">Select a simulation...</option>
                          {recentSessions?.map((session) => (
                            <option key={session.sessionId} value={session.sessionId}>
                              {new Date(session.startedAt).toLocaleString()} ({session.messages.length} messages)
                            </option>
                          ))}
                        </select>
                        {isLoadingSessions && <p className="text-sm text-gray-500 mt-1">Loading sessions...</p>}
                        {recentSessions?.length === 0 && <p className="text-sm text-gray-500 mt-1">No recent simulations found</p>}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="btn btn-secondary text-smd mr-2"
                      onClick={handleCloseDialog}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary text-smd"
                      disabled={!promptDescription.trim() || isGenerating}
                      onClick={generateCustomPrompt}
                    >
                      {isGenerating ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Generating...
                        </>
                      ) : (
                        <>Generate Prompt</>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                // Prompt Review Screen
                <>
                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-800 mb-2">Review Generated Prompt</h3>
                    <p className="text-gray-600 mb-3">
                      {promptGenerationMode === 'modify' 
                        ? 'Review the proposed changes to your prompt. You can accept or reject each change individually by clicking the buttons next to each change.'
                        : `Review the ${activePromptType === 'primary' ? 'decision maker' : 'response generator'} prompt below.`}
                      You can apply it to replace your current prompt or go back to make changes.
                    </p>
                  </div>
                  
                  {promptGenerationMode === 'modify' && diffData && (
                    <div className="mb-6">
                      <div className="flex justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-700 flex items-center">
                          <span className="mr-2">Prompt Changes:</span>
                          <button
                            className="text-smd bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full hover:bg-blue-200"
                            onClick={() => setShowDiffView(!showDiffView)}
                          >
                            {showDiffView ? 'Hide Changes' : 'Show Changes'}
                          </button>
                          
                          {/* Add button to toggle raw response view if we have raw changes */}
                          {rawResponse && (
                            <button
                              className="text-smd bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full hover:bg-purple-200 ml-2"
                              onClick={toggleRawResponseView}
                            >
                              {showRawResponse ? 'Hide Raw Changes' : 'Show Raw Changes'}
                            </button>
                          )}
                        </h3>
                        
                        <div className="flex space-x-4">
                          <button
                            className="text-smd flex items-center text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedPrompt);
                              toast.success('Prompt copied to clipboard!');
                            }}
                          >
                            <ClipboardIcon className="h-4 w-4 mr-1" />
                            Copy Result
                          </button>
                        </div>
                      </div>
                      
                      {/* Always show raw response action button when available and contains modification tags */}
                      {rawResponse && 
                       (rawResponse.includes('[ADD:') || 
                        rawResponse.includes('[REPLACE:') || 
                        rawResponse.includes('[REMOVE:')) && (
                        <div className="mb-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-md p-3 flex justify-between items-center">
                          <div className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <p className="font-medium text-indigo-700">
                                AI suggested changes with modification tags detected
                              </p>
                              <p className="text-sm text-indigo-600">
                                View and apply the raw changes with [ADD:], [REPLACE:], and [REMOVE:] tags
                              </p>
                            </div>
                          </div>
                          <button
                            className={`px-3 py-1.5 rounded-md text-sm font-medium shadow-sm 
                              ${showRawResponse 
                                ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border border-indigo-300' 
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                            onClick={toggleRawResponseView}
                          >
                            {showRawResponse ? 'Hide Raw Changes' : 'View Raw AI Changes'}
                          </button>
                        </div>
                      )}
                      
                      {showDiffView && !showRawResponse && (
                        <DiffView 
                          diffData={diffData} 
                          originalPrompt={activePromptType === 'primary' ? primaryPrompt.systemPrompt : secondaryPrompt.systemPrompt}
                          onChange={(updatedPrompt) => setGeneratedPrompt(updatedPrompt)}
                        />
                      )}
                      
                      {/* Display raw response with tags if available and selected */}
                      {showRawResponse && rawResponse && (
                        <div className="bg-purple-100 border-2 border-purple-400 rounded-md p-4 mb-4 shadow-md">
                          <div className="flex items-center mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <h4 className="font-medium text-purple-800 text-lg">Raw AI Changes Found</h4>
                          </div>
                          
                          {/* Add indicator to show raw response length to aid in debugging */}
                          <div className="bg-purple-50 border border-purple-200 rounded-md p-2 mb-4 text-xs text-purple-700">
                            <p>Raw Response Length: {rawResponse.length} characters</p>
                            <p>Contains Tags: {(rawResponse.includes('[ADD:') || rawResponse.includes('[REPLACE:') || rawResponse.includes('[REMOVE:')) ? 'Yes' : 'No'}</p>
                          </div>
                          
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <p className="text-sm text-yellow-700">
                                  <strong>Unable to automatically apply changes:</strong> The AI suggested improvements using modification tags, but they couldn't be applied automatically because some text sections weren't found in your original prompt.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                            <div className="flex">
                              <div className="flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3">
                                <h5 className="text-sm font-medium text-blue-800 mb-1">What's happening here?</h5>
                                <p className="text-sm text-blue-700">
                                  The AI has generated changes to your prompt by marking additions with <code className="font-mono bg-blue-100 px-1 rounded">[ADD: ... ]</code>, 
                                  deletions with <code className="font-mono bg-blue-100 px-1 rounded">[REMOVE: ... ]</code>, and 
                                  replacements with <code className="font-mono bg-blue-100 px-1 rounded">[REPLACE: ... WITH: ... ]</code> tags.
                                </p>
                                <p className="text-sm text-blue-700 mt-1">
                                  Click <strong>Apply Raw Changes</strong> below to process these tags and get a clean version with all changes applied.
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="border border-purple-200 rounded-md bg-white p-3 overflow-x-auto mt-3 mb-4 max-h-96">
                            <pre className="text-xs text-gray-800 whitespace-pre-wrap">{rawResponse}</pre>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="bg-gray-50 border border-gray-200 rounded p-3">
                              <h5 className="font-medium text-gray-700 mb-2">Tag Processing Rules:</h5>
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                <li><code className="bg-gray-100 text-purple-700 px-1">[ADD: text]</code> - Added as new content</li>
                                <li><code className="bg-gray-100 text-purple-700 px-1">[REMOVE: text]</code> - Removed completely</li>
                                <li><code className="bg-gray-100 text-purple-700 px-1">[REPLACE: old WITH: new]</code> - Replaced with new content</li>
                              </ul>
                            </div>
                            
                            <div className="bg-purple-50 border border-purple-200 rounded p-3">
                              <h5 className="font-medium text-purple-700 mb-2">What Happens When Applied:</h5>
                              <ul className="list-disc list-inside text-sm text-purple-700 space-y-1">
                                <li>All tags will be processed automatically</li>
                                <li>A clean version will be generated with changes applied</li>
                                <li>Any explanatory text will be preserved as comments</li>
                                <li>You'll still be able to review before saving</li>
                              </ul>
                            </div>
                          </div>
                          
                          <div className="flex justify-center mt-4">
                            <button
                              className="px-4 py-2 bg-purple-600 text-white text-base font-medium rounded-md hover:bg-purple-700 shadow-md flex items-center"
                              onClick={applyRawChanges}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Apply Raw Changes
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className={`mb-6 ${(promptGenerationMode === 'modify' && showDiffView) ? 'mt-6' : ''}`}>
                    <div className="flex justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-700 flex items-center">
                        <span className="mr-2">{promptGenerationMode === 'modify' ? 'Final Prompt:' : 'Generated Prompt:'}</span>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                          {activePromptType === 'primary' ? 'Decision Maker' : 'Response Generator'}
                        </span>
                      </h3>
                      
                      <div className="flex space-x-4">
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                          onClick={() => {
                            navigator.clipboard.writeText(generatedPrompt);
                            toast.success('Prompt copied to clipboard!');
                          }}
                        >
                          <ClipboardIcon className="h-4 w-4 mr-1" />
                          Copy
                        </button>
                      </div>
                    </div>
                    
                    <div className="relative">
                      <CodeEditor
                        value={generatedPrompt}
                        className="min-h-[600px] max-h-[800px] overflow-y-auto"
                        language="markdown"
                        padding={15}
                        readOnly={true}
                        style={{
                          fontSize: 12,
                          backgroundColor: "#f5f5f5",
                          fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Consolas, Liberation Mono, Menlo, monospace',
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* API Analysis Section */}
                  {apiAnalysis && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">API Endpoint Analysis:</h3>
                      
                      <div className={`p-4 rounded-md ${apiAnalysis.successful ? 'bg-green-50' : 'bg-yellow-50'}`}>
                        <div className="flex">
                          <div className="flex-shrink-0">
                            {apiAnalysis.successful ? (
                              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-3">
                            <p className={`text-sm ${apiAnalysis.successful ? 'text-green-700' : 'text-yellow-700'}`}>
                              {apiAnalysis.message}
                            </p>
                            
                            {apiAnalysis.suggestions && apiAnalysis.suggestions.length > 0 && (
                              <div className="mt-2">
                                <h4 className="font-medium text-sm">Suggestions:</h4>
                                <ul className="list-disc pl-5 mt-1 text-sm text-gray-600 space-y-1">
                                  {apiAnalysis.suggestions.map((suggestion, index) => (
                                    <li key={index}>{suggestion}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="btn btn-secondary text-smd"
                      onClick={() => {
                        setIsReviewMode(false);
                        setGeneratedPrompt('');
                        setShowDiffView(false);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary text-smd ml-2"
                      onClick={applyGeneratedPrompt}
                    >
                      Apply Prompt
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PromptBuilder;